import { computed, inject, Injectable, signal } from '@angular/core';
import { UserService } from './user.service';
import { CmsApiService } from '../api/cms-api.service';
import { filter, map } from 'rxjs/operators';

export type UserResults = {
  userBiggestWin: string,
  sumOfAllWinsOfUser: string,
  userLastWin: string,
  userBiggestWinGame: {image: string, title: string, provider: string, slug: string},
  userWinCount: string,
  userMaxDailyWin: string,
  userLastMonthWin: string,
  userCurrentMonthWin: string,
  userWinDiffPercentLastVsCurrent: string | number,
  userTopMultiplierGames: {image: string, title: string, provider: string, x:number}[],
  userTotalBetsCount: string,
  userMaxWinStreak: string,
  userSlotCount: string,
  userBiggestWinDay: string,
  userFreespinsTotal: number,
  userFreespinsUsed: number,
  isNegativeLastVsCurrent: boolean,
}

@Injectable({
  providedIn: 'root',
})
export class UserResultsService {

  private _user = inject(UserService);
  private _cmsApi = inject(CmsApiService);

  private _state = signal<UserResults>({
    userBiggestWin: null,
    sumOfAllWinsOfUser: null,
    userLastWin: null,
    userBiggestWinGame: null,
    userWinCount: null,
    userMaxDailyWin: null,
    userLastMonthWin: null,
    userCurrentMonthWin: null,
    userWinDiffPercentLastVsCurrent: null,
    userTopMultiplierGames: [null, null, null],
    userTotalBetsCount: null,
    userMaxWinStreak: null,
    userSlotCount: null,
    userBiggestWinDay: null,
    userFreespinsTotal: null,
    userFreespinsUsed: null,
    isNegativeLastVsCurrent: null,
  });

  private _isLoadedState = signal<boolean>(false);
  public isLoadedState = computed(() => this._isLoadedState());

  public userBiggestWin = computed(() => this._state().userBiggestWin);
  public sumOfAllWinsOfUser = computed(() => this._state().sumOfAllWinsOfUser);
  public userLastWin = computed(() => this._state().userLastWin);
  public userBiggestWinGame = computed(() => this._state().userBiggestWinGame);
  public userWinCount = computed(() => this._state().userWinCount);
  public userMaxDailyWin = computed(() => this._state().userMaxDailyWin);
  public userLastMonthWin = computed(() => this._state().userLastMonthWin);
  public userCurrentMonthWin = computed(() => this._state().userCurrentMonthWin);
  public userWinDiffPercentLastVsCurrent = computed(() => this._state().userWinDiffPercentLastVsCurrent);
  public userTopMultiplierGames = computed(() => this._state().userTopMultiplierGames);
  public userTotalBetsCount = computed(() => this._state().userTotalBetsCount);
  public userMaxWinStreak = computed(() => this._state().userMaxWinStreak);
  public userSlotCount = computed(() => this._state().userSlotCount);
  public userBiggestWinDay = computed(() => this._state().userBiggestWinDay);
  public userFreespinsTotal = computed(() => this._state().userFreespinsTotal);
  public userFreespinsUsed = computed(() => this._state().userFreespinsUsed);
  public isNegativeLastVsCurrent = computed(() => this._state().isNegativeLastVsCurrent);

  constructor() {}

  public getUserResults(): void {
    this._cmsApi.playerBetStatisticNew({
      player_id: this._user.info.id,
      currency: this._user.info.currency,
    }).pipe(
      filter(response => !!response),
      map(response => response?.data),
    ).subscribe((userResults: UserResults) => {
      this._setUserResults(userResults);
      this._isLoadedState.update(() => true);
    })
  }

  private _setUserResults(userResults: UserResults) {
    this._state.update(state => {
      if (userResults.userTopMultiplierGames) {
        userResults.userTopMultiplierGames.forEach((g, i) => state.userTopMultiplierGames[i] = g)
      }
      return {
        ...state,
        ...userResults,
        userTopMultiplierGames: state.userTopMultiplierGames,
        userFreespinsUsed: Number(userResults?.userFreespinsUsed),
        userFreespinsTotal: Number(userResults?.userFreespinsTotal),
        userWinDiffPercentLastVsCurrent: Math.abs(Number(userResults?.userWinDiffPercentLastVsCurrent)),
        isNegativeLastVsCurrent: Number(userResults?.userWinDiffPercentLastVsCurrent) < 0
      };
    });
  }
}
